/**
 * 이모지만으로 이루어진 짧은 메시지 판정 — 말풍선 없이 크게 렌더하기 위한 것.
 * 카카오톡·슬랙·왓츠앱이 모두 쓰는 동작이다.
 *
 * 반환값은 곧 이모지 개수이자 크기 단계다(1이 가장 큼). 0이면 평소 렌더.
 * 판정에 걸리는 조건은 "이모지 1~3개 + 공백"뿐 — 글자가 하나라도 섞이면 0이다.
 */

/** 이 개수를 넘으면 평소 크기로 돌아간다 */
const MAX_BIG_EMOJI = 3;

/**
 * `\p{...}` 유니코드 속성 이스케이프는 ES2018이라 구형 엔진에서 SyntaxError가 난다.
 * 정규식 리터럴로 쓰면 파싱 시점에 모듈 전체가 죽으므로 new RegExp + try/catch로 감싸고,
 * 실패하면 세 값이 모두 null로 남아 기능이 조용히 꺼진다(평소 렌더로 폴백).
 *
 * ALLOWED  — 이모지 자소를 이룰 수 있는 코드포인트 전체.
 *            0-9·#·*는 keycap(1️⃣) 베이스라 허용하되, PICTO 검사가 맨숫자를 걸러낸다.
 * PICTO    — "이게 진짜 이모지인가" 판정. 자소마다 최소 하나는 있어야 한다.
 * MODIFIER — 피부톤(👍🏽의 🏽). Segmenter 없는 엔진의 폴백 분해에서 쓴다.
 */
let ALLOWED: RegExp | null = null;
let PICTO: RegExp | null = null;
let MODIFIER: RegExp | null = null;
try {
  ALLOWED = new RegExp(
    '^[\\p{Extended_Pictographic}\\p{Emoji_Modifier}\\p{Regional_Indicator}0-9#*\uFE0E\uFE0F\u200D\u20E3]+$',
    'u'
  );
  PICTO = new RegExp('[\\p{Extended_Pictographic}\\p{Regional_Indicator}\u20E3]', 'u');
  MODIFIER = new RegExp('\\p{Emoji_Modifier}', 'u');
} catch {
  ALLOWED = null;
  PICTO = null;
  MODIFIER = null;
}

/** ZWJ·변이선택자·피부톤·keycap을 앞 자소에 붙이는 근사 분해 (Intl.Segmenter 없는 엔진용) */
function clusterFallback(text: string): string[] {
  const out: string[] = [];
  for (const cp of Array.from(text)) {
    const last = out.length > 0 ? out[out.length - 1] : null;
    const isCombining =
      cp === '\u200D' || cp === '\uFE0F' || cp === '\uFE0E' || cp === '\u20E3' ||
      (MODIFIER !== null && MODIFIER.test(cp));
    // 직전 자소가 ZWJ로 끝나면 이번 코드포인트는 같은 자소의 연속이다 (가족 이모지)
    if (last !== null && (isCombining || last.endsWith('\u200D'))) {
      out[out.length - 1] = last + cp;
      continue;
    }
    out.push(cp);
  }
  return out;
}

/** 자소(grapheme) 단위 분해 — ZWJ 가족·피부톤·keycap이 각각 1개로 세어져야 한다 */
function toGraphemes(text: string): string[] {
  const Segmenter = (Intl as any)?.Segmenter;
  if (typeof Segmenter === 'function') {
    try {
      const seg = new Segmenter('ko', { granularity: 'grapheme' });
      return Array.from(seg.segment(text), (s: any) => s.segment as string);
    } catch {
      // 폴백으로 진행
    }
  }
  return clusterFallback(text);
}

/**
 * 이모지만 있는 메시지면 그 개수(1~3), 아니면 0.
 * ZWJ 가족·피부톤·VS16·keycap 조합은 각각 1개로 센다.
 */
export function getEmojiOnlyLevel(text: string | null | undefined): 0 | 1 | 2 | 3 {
  if (!text || ALLOWED === null || PICTO === null) return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  // 이모지 3개는 결합 문자를 다 붙여도 60코드유닛을 넘지 않는다.
  // 긴 본문에 자소 분해를 돌리지 않기 위한 빠른 탈출 (메시지 목록 전체를 매 렌더 훑는다).
  if (trimmed.length > 60) return 0;

  let count = 0;
  for (const g of toGraphemes(trimmed)) {
    if (!g.trim()) continue;      // 이모지 사이 공백은 허용
    if (!ALLOWED.test(g)) return 0;
    if (!PICTO.test(g)) return 0; // 맨숫자('1')처럼 keycap 없는 베이스 문자를 걸러낸다
    count++;
    if (count > MAX_BIG_EMOJI) return 0;
  }
  return count as 0 | 1 | 2 | 3;
}
